import { useEffect, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useTranslation } from '@/app/hooks/useTranslation';
import { Check, ChevronDown, Image as ImageIcon, Trash2, Upload, X } from 'lucide-react';

type FieldType = 'text' | 'textarea' | 'number' | 'toggle' | 'select' | 'multiselect' | 'status' | 'date' | 'time' | 'datetime-local' | 'password' | 'email' | 'tel' | 'map' | 'image';
type ConfigurationSelectOption = { value: string; label: string };
type ConfigurationFieldAction = {
  label: string;
  loadingLabel?: string;
  successMessage?: string;
  onClick: (draft: Record<string, any>) => Promise<Record<string, any>> | Record<string, any>;
};

export interface ConfigurationFieldDefinition {
  key: string;
  label: string;
  getLabel?: (draft: Record<string, any>) => string;
  type?: FieldType;
  required?: boolean;
  placeholder?: string;
  options?: ConfigurationSelectOption[];
  getOptions?: (draft: Record<string, any>) => ConfigurationSelectOption[];
  onValueChange?: (value: unknown, draft: Record<string, any>) => Record<string, any>;
  action?: ConfigurationFieldAction;
  uploadImage?: (file: File, draft: Record<string, any>) => Promise<string>;
  removeImage?: (draft: Record<string, any>) => Promise<void>;
  accept?: string;
  mapFields?: { latitudeKey: string; longitudeKey: string };
  readOnly?: boolean;
  hidden?: boolean;
  visibleWhen?: (draft: Record<string, any>) => boolean;
  helperText?: string;
  maxLength?: number;
  min?: number;
  max?: number;
}

interface ConfigurationDetailSheetProps<T extends Record<string, any>> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  title: string;
  description: string;
  fields: ConfigurationFieldDefinition[];
  value: T | null;
  onSave: (next: T) => Promise<void>;
  onDelete?: (value: T) => Promise<void>;
  deleteLabel?: string;
  renderAfterFields?: (draft: Record<string, any>, setDraft: Dispatch<SetStateAction<Record<string, any>>>) => ReactNode;
}

const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export function ConfigurationDetailSheet<T extends Record<string, any>>({
  open,
  onOpenChange,
  mode,
  title,
  description,
  fields,
  value,
  onSave,
  onDelete,
  deleteLabel,
  renderAfterFields
}: ConfigurationDetailSheetProps<T>) {
  const { t: translate } = useTranslation();
  const tr = (key: string, params?: Record<string, unknown>) => translate('configuration', key, params);
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [runningActionKey, setRunningActionKey] = useState<string | null>(null);
  const [uploadingImageKey, setUploadingImageKey] = useState<string | null>(null);
  const [openChoiceKey, setOpenChoiceKey] = useState<string | null>(null);

  useEffect(() => {
    setDraft(value ?? {});
    setErrors({});
    setOpenChoiceKey(null);
    setDeleteArmed(false);
  }, [value, open]);

  const formTitle = useMemo(
    () => tr(mode === 'create' ? 'createFormTitle' : 'editFormTitle', { title }),
    [mode, title]
  );
  const visibleFields = useMemo(
    () => fields.filter((field) => !field.hidden && (field.visibleWhen ? field.visibleWhen(draft) : true)),
    [draft, fields]
  );
  const fieldLabel = (field: ConfigurationFieldDefinition) => field.getLabel?.(draft) ?? field.label;
  const hasSlugField = useMemo(() => fields.some((field) => field.key === 'slug' && !field.hidden), [fields]);

  const setFieldValue = (field: ConfigurationFieldDefinition, value: unknown) => {
    setDraft((prev) => {
      const nextValue = field.key === 'slug' ? slugify(String(value ?? '')) : value;
      const next = { ...prev, [field.key]: nextValue };

      if (hasSlugField && field.key === 'name' && !String(prev.slug ?? '').trim()) {
        next.slug = slugify(String(value ?? ''));
      }

      const linkedValues = field.onValueChange?.(nextValue, next);
      if (linkedValues) {
        Object.assign(next, linkedValues);
      }

      return next;
    });
  };

  const helperByType = (field: ConfigurationFieldDefinition) => {
    if (field.helperText) return field.helperText;
    if (field.type === 'email') return tr('emailFormat');
    if (field.type === 'date') return tr('dateFormat');
    if (field.type === 'time') return tr('timeFormat');
    if (field.type === 'datetime-local') return tr('datetimeFormat');
    return null;
  };

  const runFieldAction = async (field: ConfigurationFieldDefinition) => {
    if (!field.action) return;

    try {
      setRunningActionKey(field.key);
      const patch = await field.action.onClick(draft);
      setDraft((prev) => ({ ...prev, ...patch }));
      if (field.action.successMessage) toast.success(field.action.successMessage);
    } catch (error: any) {
      toast.error(error?.message ?? tr('actionFailed'));
    } finally {
      setRunningActionKey(null);
    }
  };

  const uploadFieldImage = async (field: ConfigurationFieldDefinition, file: File | null) => {
    if (!file || !field.uploadImage) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast.error('Seleciona uma imagem JPG, PNG ou WebP.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('A imagem não pode exceder 8 MB.');
      return;
    }
    try {
      setUploadingImageKey(field.key);
      const imageUrl = await field.uploadImage(file, draft);
      setFieldValue(field, imageUrl);
      toast.success('Imagem carregada.');
    } catch (error: any) {
      toast.error(error?.message ?? 'Não foi possível carregar a imagem.');
    } finally {
      setUploadingImageKey(null);
    }
  };

  const removeFieldImage = async (field: ConfigurationFieldDefinition) => {
    try {
      setUploadingImageKey(field.key);
      await field.removeImage?.(draft);
      setFieldValue(field, null);
      toast.success('Imagem removida.');
    } catch (error: any) {
      toast.error(error?.message ?? 'Não foi possível remover a imagem.');
    } finally {
      setUploadingImageKey(null);
    }
  };

  const describeSaveError = (error: any) => {
    const message = String(error?.message ?? '');
    const isLengthError =
      error?.code === '22001' ||
      /value too long|character varying|string data|tamanho|comprimento/i.test(message);

    if (isLengthError) {
      const oversizedField = visibleFields.find((field) => {
        if (!field.maxLength) return false;
        const current = draft[field.key];
        return current != null && String(current).length > field.maxLength;
      });

      if (oversizedField?.maxLength) {
        return tr('maxLengthField', {
          field: oversizedField.label,
          max: oversizedField.maxLength,
          count: String(draft[oversizedField.key] ?? '').length
        });
      }

      return tr('textFieldTooLong');
    }

    return message || tr('saveFailed', { title });
  };

  const handleSave = async () => {
    const nextErrors: Record<string, string> = {};
    visibleFields.forEach((field) => {
      const current = draft[field.key];
      if (
        field.required &&
        (current === undefined ||
          current === null ||
          (Array.isArray(current) ? current.length === 0 : String(current).trim() === ''))
      ) {
        nextErrors[field.key] = tr('requiredField', { field: fieldLabel(field) });
      }
      if (field.type === 'email' && current && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(current).trim())) {
        nextErrors[field.key] = tr('invalidEmail');
      }
      if (field.type === 'tel' && current && !/^\+[1-9]\d{7,14}$/.test(String(current).replace(/[\s().-]/g, ''))) {
        nextErrors[field.key] = tr('invalidPhone');
      }
      if (field.maxLength && current != null && String(current).length > field.maxLength) {
        nextErrors[field.key] = tr('maxLengthField', {
          field: fieldLabel(field),
          max: field.maxLength,
          count: String(current).length
        });
      }
      if (field.type === 'number' && current !== undefined && current !== null && current !== '') {
        const parsed = Number(current);
        if (!Number.isFinite(parsed)) {
          nextErrors[field.key] = tr('invalidNumber', { field: fieldLabel(field) });
        } else if (field.min != null && parsed < field.min) {
          nextErrors[field.key] = tr('minNumberField', { field: fieldLabel(field), min: field.min });
        } else if (field.max != null && parsed > field.max) {
          nextErrors[field.key] = tr('maxNumberField', { field: fieldLabel(field), max: field.max });
        }
      }
    });

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast.error(tr('fixFormErrors'));
      return;
    }
    try {
      setSaving(true);
      await onSave(draft as T);
      toast.success(tr('saved', { title }));
      onOpenChange(false);
    } catch (error: any) {
      toast.error(describeSaveError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || !value || mode !== 'edit') return;
    if (!deleteArmed) {
      setDeleteArmed(true);
      return;
    }

    try {
      setDeleting(true);
      await onDelete(value);
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.message ?? tr('deleteRecordFailed'));
    } finally {
      setDeleting(false);
      setDeleteArmed(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 border-l border-border bg-card text-foreground sm:max-w-[680px]">
        <SheetHeader className="border-b border-border px-5 py-4 pr-12">
          <SheetTitle className="text-xl font-semibold tracking-tight">{formTitle}</SheetTitle>
          <SheetDescription className="mt-1 max-w-[56ch] text-sm leading-5">{description}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          {visibleFields.map((field) => {
            const resolvedLabel = fieldLabel(field);
            const fieldType = field.type ?? 'text';
            const current = draft[field.key];
            const disabled = Boolean(field.readOnly);
            const selectOptions = fieldType === 'select' || fieldType === 'multiselect'
              ? field.getOptions?.(draft) ?? field.options ?? []
              : [];
            const currentValues = Array.isArray(current)
              ? current.map(String)
              : String(current ?? '').split(',').map((item) => item.trim()).filter(Boolean);
            const selectedOption = selectOptions.find((option) => option.value === String(current ?? ''));
            const selectedOptions = selectOptions.filter((option) => currentValues.includes(option.value));
            const controlChrome = errors[field.key]
              ? 'border-red-400/70 bg-red-50/70 dark:bg-red-500/5'
              : 'border-border bg-muted/35 hover:border-border/90 hover:bg-muted/55 dark:border-white/7 dark:bg-slate-950/18 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] dark:hover:border-white/12 dark:hover:bg-slate-950/24';
            const focusChrome = 'focus:border-primary/55 focus:bg-background focus:ring-2 focus:ring-primary/12 dark:focus:bg-slate-950/24';
            const choiceButtonClass = `group flex min-h-10 w-full items-center justify-between gap-3 rounded-lg border px-3.5 py-2 text-left text-sm text-foreground outline-none transition-all ${controlChrome} ${focusChrome} ${
              disabled ? 'cursor-not-allowed opacity-65' : 'cursor-pointer'
            }`;
            const choicePanelClass = 'mt-2 overflow-hidden rounded-lg border border-border bg-popover/98 p-1 shadow-xl shadow-slate-950/25 backdrop-blur';
            const inputClass = `h-10 w-full rounded-lg border px-3.5 text-sm text-foreground outline-none transition-all ${controlChrome} ${focusChrome} disabled:cursor-not-allowed disabled:opacity-65`;
            const textAreaClass = `w-full rounded-lg border px-3.5 py-2.5 text-sm leading-6 text-foreground outline-none transition-all ${controlChrome} ${focusChrome} disabled:cursor-not-allowed disabled:opacity-65`;
            const fieldGapClass = helperByType(field) ? 'space-y-2' : 'space-y-2.5';

            return (
              <div key={field.key} className={fieldGapClass}>
                <label className="block text-[13px] font-semibold leading-none text-foreground">{resolvedLabel}</label>

                {fieldType === 'image' ? (
                  <div className="overflow-hidden rounded-lg border border-border bg-muted/20 dark:border-white/7 dark:bg-slate-950/18">
                    {current ? (
                      <div className="relative aspect-[16/5] overflow-hidden bg-slate-950">
                        <img src={String(current)} alt="Pré-visualização da imagem de capa" className="h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/45 via-transparent to-slate-950/10" />
                      </div>
                    ) : (
                      <div className="flex aspect-[16/5] items-center justify-center gap-2 text-sm text-muted-foreground">
                        <ImageIcon className="h-5 w-5" />
                        Sem imagem de capa
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 border-t border-border p-3 dark:border-white/7">
                      <label className={`inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg bg-primary px-3.5 text-xs font-semibold text-primary-foreground transition-opacity ${uploadingImageKey === field.key || disabled ? 'pointer-events-none opacity-55' : 'hover:opacity-90'}`}>
                        <Upload className="h-4 w-4" />
                        {uploadingImageKey === field.key ? 'A carregar…' : current ? 'Substituir imagem' : 'Carregar imagem'}
                        <input
                          type="file"
                          accept={field.accept ?? 'image/jpeg,image/png,image/webp'}
                          className="sr-only"
                          disabled={disabled || uploadingImageKey === field.key}
                          onChange={(event) => {
                            void uploadFieldImage(field, event.target.files?.[0] ?? null);
                            event.currentTarget.value = '';
                          }}
                        />
                      </label>
                      {current ? (
                        <button
                          type="button"
                          onClick={() => void removeFieldImage(field)}
                          disabled={disabled || uploadingImageKey === field.key}
                          className="inline-flex h-9 items-center gap-2 rounded-lg border border-red-300/60 px-3.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50 disabled:opacity-55 dark:border-red-400/25 dark:text-red-300 dark:hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                          Remover
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : fieldType === 'map' ? (
                  (() => {
                    const latitudeKey = field.mapFields?.latitudeKey ?? 'latitude';
                    const longitudeKey = field.mapFields?.longitudeKey ?? 'longitude';
                    const latitude = Number(draft[latitudeKey]);
                    const longitude = Number(draft[longitudeKey]);
                    const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
                    const mapUrl = hasCoordinates
                      ? `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - 0.01}%2C${latitude - 0.01}%2C${longitude + 0.01}%2C${latitude + 0.01}&layer=mapnik&marker=${latitude}%2C${longitude}`
                      : '';

                    return hasCoordinates ? (
                      <div className="overflow-hidden rounded-lg border border-border bg-muted/20 dark:border-white/7 dark:bg-slate-950/18">
                        <iframe
                          title={resolvedLabel}
                          src={mapUrl}
                          className="h-64 w-full border-0"
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      </div>
                    ) : (
                      <div className="flex h-36 items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 px-4 text-center text-sm text-muted-foreground dark:border-white/10 dark:bg-slate-950/12">
                        {tr('mapPreviewUnavailable')}
                      </div>
                    );
                  })()
                ) : fieldType === 'textarea' ? (
                  <textarea
                    value={typeof current === 'object' && current !== null ? JSON.stringify(current, null, 2) : current ?? ''}
                    onChange={(event) => setFieldValue(field, event.target.value)}
                    placeholder={field.placeholder}
                    rows={4}
                    maxLength={field.maxLength}
                    disabled={disabled}
                    className={textAreaClass}
                  />
                ) : fieldType === 'toggle' ? (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={Boolean(current)}
                    onClick={() => {
                      if (disabled) return;
                      setDraft((prev) => ({ ...prev, [field.key]: !current }));
                    }}
                    disabled={disabled}
                    className={`flex w-full items-center justify-between gap-4 rounded-lg border px-3.5 py-2 text-left transition-all ${controlChrome} ${focusChrome} ${
                      disabled ? 'cursor-not-allowed opacity-65' : 'cursor-pointer'
                    }`}
                  >
                    <span className={`text-sm font-semibold ${current ? 'text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground'}`}>
                      {current ? tr('active') : tr('inactive')}
                    </span>
                    <span
                      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                        current
                          ? 'bg-sky-400/45'
                          : 'bg-slate-700/70'
                      }`}
                    >
                      <span
                        className={`absolute top-1 h-4 w-4 rounded-full shadow-sm transition-transform ${
                          current
                            ? 'translate-x-6 bg-sky-50'
                            : 'translate-x-1 bg-slate-300/80'
                        }`}
                      />
                    </span>
                  </button>
                ) : fieldType === 'status' ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => !disabled && setOpenChoiceKey((key) => key === field.key ? null : field.key)}
                      disabled={disabled}
                      className={choiceButtonClass}
                    >
                      <span className="font-medium">{current ? tr('active') : tr('inactive')}</span>
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${openChoiceKey === field.key ? 'rotate-180' : ''}`} />
                    </button>
                    {openChoiceKey === field.key ? (
                      <div className={choicePanelClass}>
                        {[
                          { value: 'active', label: tr('active') },
                          { value: 'inactive', label: tr('inactive') }
                        ].map((option) => {
                          const active = option.value === (current ? 'active' : 'inactive');
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setDraft((prev) => ({ ...prev, [field.key]: option.value === 'active' }));
                                setOpenChoiceKey(null);
                              }}
                              className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${
                                active ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:bg-muted/45 hover:text-foreground'
                              }`}
                            >
                              <span>{option.label}</span>
                              {active ? <Check className="h-4 w-4 text-primary" /> : null}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : fieldType === 'select' ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => !disabled && setOpenChoiceKey((key) => key === field.key ? null : field.key)}
                      disabled={disabled}
                      className={choiceButtonClass}
                    >
                      <span className={`truncate font-medium ${selectedOption ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {selectedOption?.label ?? field.placeholder ?? tr('selectPlaceholder')}
                      </span>
                      <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${openChoiceKey === field.key ? 'rotate-180' : ''}`} />
                    </button>
                    {openChoiceKey === field.key ? (
                      <div className={choicePanelClass}>
                        {!field.required ? (
                          <button
                            type="button"
                            onClick={() => {
                              setFieldValue(field, '');
                              setOpenChoiceKey(null);
                            }}
                            className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${
                              !current ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:bg-muted/45 hover:text-foreground'
                            }`}
                          >
                            <span>{tr('selectPlaceholder')}</span>
                            {!current ? <Check className="h-4 w-4 text-primary" /> : null}
                          </button>
                        ) : null}
                        {selectOptions.map((option) => {
                          const active = option.value === String(current ?? '');
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setFieldValue(field, option.value);
                                setOpenChoiceKey(null);
                              }}
                              className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${
                                active ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:bg-muted/45 hover:text-foreground'
                              }`}
                            >
                              <span className="truncate">{option.label}</span>
                              {active ? <Check className="h-4 w-4 text-primary" /> : null}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : fieldType === 'multiselect' ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => !disabled && setOpenChoiceKey((key) => key === field.key ? null : field.key)}
                      disabled={disabled}
                      className={choiceButtonClass}
                    >
                      <span className={`min-w-0 ${selectedOptions.length ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {selectedOptions.length ? (
                          <span className="flex flex-wrap gap-1.5">
                            {selectedOptions.slice(0, 3).map((option) => (
                              <span key={option.value} className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                                {option.label}
                              </span>
                            ))}
                            {selectedOptions.length > 3 ? (
                              <span className="rounded-full bg-muted/45 px-2.5 py-1 text-xs font-semibold text-muted-foreground">
                                +{selectedOptions.length - 3}
                              </span>
                            ) : null}
                          </span>
                        ) : (
                          field.placeholder ?? tr('selectPlaceholder')
                        )}
                      </span>
                      <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${openChoiceKey === field.key ? 'rotate-180' : ''}`} />
                    </button>
                    {openChoiceKey === field.key ? (
                      <div className={choicePanelClass}>
                        {selectOptions.map((option) => {
                          const active = currentValues.includes(option.value);
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                const next = active
                                  ? currentValues.filter((value) => value !== option.value)
                                  : [...currentValues, option.value];
                                setFieldValue(field, next);
                              }}
                              className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${
                                active ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:bg-muted/45 hover:text-foreground'
                              }`}
                            >
                              <span className="truncate">{option.label}</span>
                              <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${
                                active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-muted/20'
                              }`}>
                                {active ? <Check className="h-3.5 w-3.5" /> : null}
                              </span>
                            </button>
                          );
                        })}
                        {selectedOptions.length ? (
                          <button
                            type="button"
                            onClick={() => setFieldValue(field, [])}
                            className="mt-1 flex w-full items-center gap-2 rounded-md border-t border-border/70 px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-muted/45 hover:text-foreground"
                          >
                            <X className="h-4 w-4" />
                            Limpar seleção
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <input
                    type={fieldType === 'number'
                      ? 'number'
                      : fieldType === 'date' || fieldType === 'time' || fieldType === 'datetime-local' || fieldType === 'password' || fieldType === 'email' || fieldType === 'tel'
                        ? fieldType
                        : 'text'}
                    value={current ?? ''}
                    onChange={(event) =>
                      setFieldValue(
                        field,
                        fieldType === 'number'
                          ? event.target.value === '' ? null : Number(event.target.value)
                          : event.target.value
                      )
                    }
                    placeholder={field.placeholder}
                    maxLength={field.maxLength}
                    min={field.min}
                    max={field.max}
                    disabled={disabled}
                    className={inputClass}
                  />
                )}

                {field.action ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => runFieldAction(field)}
                    disabled={disabled || runningActionKey === field.key}
                    className="h-10 rounded-lg"
                  >
                    {runningActionKey === field.key ? field.action.loadingLabel ?? tr('runningAction') : field.action.label}
                  </Button>
                ) : null}
                {errors[field.key] ? <p className="text-xs leading-5 text-red-600 dark:text-red-400">{errors[field.key]}</p> : null}
                {!errors[field.key] && helperByType(field) ? <p className="text-xs leading-5 text-muted-foreground">{helperByType(field)}</p> : null}
              </div>
            );
          })}
          {renderAfterFields?.(draft, setDraft)}
        </div>

        {deleteArmed ? (
          <div className="border-t border-red-200 bg-red-50/70 px-5 py-3 text-sm font-medium text-red-800 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200">
            Esta ação é permanente e remove também relações dependentes quando existirem.
          </div>
        ) : null}

        <SheetFooter className={`grid gap-3 border-t border-border px-5 py-4 ${onDelete && mode === 'edit' ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
          {onDelete && mode === 'edit' ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleDelete}
              disabled={saving || deleting}
              className={`h-11 rounded-xl text-sm transition-colors ${
                deleteArmed
                  ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200'
                  : 'text-red-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700 dark:text-red-300 dark:hover:bg-red-500/10'
              }`}
            >
              {deleting ? 'A apagar...' : deleteArmed ? 'Confirmar apagar' : deleteLabel ?? 'Apagar'}
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving || deleting} className="h-11 rounded-xl text-sm">
            {translate('common', 'cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving || deleting} className="h-11 rounded-xl text-sm font-semibold">
            {saving ? tr('saving') : translate('common', 'save')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
